import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import {
  resetLuckySpinState,
  updateLuckySpinRequest,
  fetchRunningSipsRequest,
  fetchSpinHistoryRequest,
} from "../../../redux/SipPlan/SipPlanSlice";
import { controlAllFeatureOptions } from "../../../configs/constants";
import { toast } from "react-toastify";

// *** IMPORTANT: Update this import to your actual Redux action ***
// Assuming this is still the correct action, uncomment when ready
// import { recordLuckyWinnerRequest } from '../store/slices/sipSlice';

// ------------------------------------------------
// 1. STYLING (MUI Box and CSS Keyframes) - No Change
// ------------------------------------------------

// Style for the main content box inside the modal
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1500,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  display: "flex",
  textAlign: "center",
};
// Left column (Spin History)
const LeftColumn = styled("div")({
  flex: 2,
  maxHeight: "70vh",
  overflowY: "auto",
});

// Right column (Spinner / Winner)
const RightColumn = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});
const fancySpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const bounce = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.7); }
  100% { transform: translate(-50%, -50%) scale(1.2); }
`;

const FancySpinner = styled("div")(({ isSpinning }) => ({
  width: 160,
  height: 160,
  margin: "20px auto",
  borderRadius: "50%",
  position: "relative",
  border: "6px solid transparent",
  borderTop: "6px solid #ff9800",
  borderRight: "6px solid #f44336",
  borderBottom: "6px solid #4caf50",
  borderLeft: "6px solid #2196f3",
  animation: isSpinning ? `${fancySpin} 1s linear infinite` : "none",
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 20,
    height: 20,
    backgroundColor: "#ffc107",
    borderRadius: "50%",
    transform: "translate(-50%, -50%)",
    animation: isSpinning
      ? `${bounce} 0.6s ease-in-out infinite alternate`
      : "none",
  },
}));

const HistoryGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", // 1-2 cards per row, auto fit
  gap: "20px",
  marginTop: "24px",
});
const HistoryCard = styled("div")({
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  backgroundColor: "#fafafa",
  textAlign: "left",
  fontSize: "0.95rem",
  borderLeft: "5px solid #1976d2",
});

// const HistoryCard = styled('div')({
//   padding: '12px',
//   borderRadius: '8px',
//   boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
//   backgroundColor: '#fff',
//   textAlign: 'center',
// });

// ------------------------------------------------
// 2. COMPONENT LOGIC - UPDATED
// ------------------------------------------------

const getPositionLabel = (position) =>
  position === 1
    ? `Main Winner (Position ${position})`
    : `Additional Winner (Position ${position})`;

const LuckySpinModal = ({ isOpen, onClose, plan }) => {
  const dispatch = useDispatch();
  const { luckyWinner, luckyWinners, spinHistory, runningSips, loading } =
    useSelector((state) => state.sipplan);
  const getAppConfigData = useSelector((state) => state.appConfig.configData);
  const sipMultiWinnerLuckyDrawEnabled =
    getAppConfigData?.featureToggle?.[
      controlAllFeatureOptions.Multi_Winner_Lucky_Draw
    ] === true ||
    getAppConfigData?.featureToggle?.multi_winner_lucky_draw === true;
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [manualWinners, setManualWinners] = useState({});
  const [mode, setMode] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const isMultiWinner =
    Boolean(plan?.multi_winner_lucky_draw) && sipMultiWinnerLuckyDrawEnabled;
  const winnerCount = isMultiWinner
    ? Math.max(2, Number(plan?.no_of_winners) || 2)
    : 1;
  const winnerPositions = useMemo(
    () =>
      isMultiWinner
        ? Array.from({ length: winnerCount }, (_, index) => index + 1)
        : [1],
    [isMultiWinner, winnerCount],
  );
  // winner now stores an object: { id: string, name: string }
  const [winner, setWinner] = useState(null);

  // NEW STATE: Tracks the name currently displayed in the spinning wheel
  const [currentName, setCurrentName] = useState("Ready!");

  // NEW REF: Ref to hold the interval ID for cleanup
  const intervalRef = useRef(null);

  const getCustomerObject = (sip) => {
    if (sip?.customerDetails && typeof sip.customerDetails === "object") {
      return sip.customerDetails;
    }
    if (sip?.customer && typeof sip.customer === "object") {
      return sip.customer;
    }
    return null;
  };

  const getCustomerId = (sip) => {
    const customerObj = getCustomerObject(sip);
    return customerObj?._id || sip?.customer || null;
  };

  const getCustomerName = (sip) => {
    const customerObj = getCustomerObject(sip);
    const firstName = customerObj?.firstName || "";
    const lastName = customerObj?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const serialNumber = sip?.serialNumber;
    const serialLabel =
      Number.isInteger(serialNumber) && serialNumber > 0
        ? ` (S.No: ${serialNumber})`
        : "";
    return `${fullName || "Unknown Customer"}${serialLabel}`;
  };

  const getSipWonPositions = (sipId) => {
    if (!sipId) return [];
    return (spinHistory || [])
      .filter((entry) => {
        const historySipId = entry?.commonSip?._id || entry?.commonSip || null;
        return historySipId && String(historySipId) === String(sipId);
      })
      .map((entry) => entry.position || 1);
  };

  const isEligibleForPosition = (sipId, position) => {
    const wonPositions = getSipWonPositions(sipId);
    if (position === 1) {
      return !wonPositions.includes(1);
    }
    return wonPositions.length === 0;
  };

  const eligibleRunningSips = useMemo(() => {
    const currentPlanId = plan?._id;
    return (runningSips || []).filter((sip) => {
      // Guard against stale runningSips from previously opened plans.
      if (currentPlanId && String(sip?.sipplan) !== String(currentPlanId)) {
        return false;
      }

      const isEligible =
        sip?.luckyDrawEligibility ??
        sip?.luckyDrawDetails?.eligibility ??
        true;
      const isLuckyDrawEnabled =
        sip?.sipplanDetails?.enableLuckyDraw ??
        sip?.enableLuckyDraw ??
        sip?.luckyDrawDetails?.enableLuckyDraw ??
        plan?.enableLuckyDraw ??
        true;

      return Boolean(isEligible) && Boolean(isLuckyDrawEnabled);
    });
  }, [runningSips, plan?._id, plan?.enableLuckyDraw]);

  const getCustomerIdBySipId = (sipId) => {
    const sip = eligibleRunningSips.find(
      (item) => String(item?._id) === String(sipId),
    );
    return getCustomerId(sip);
  };

  const getEligibleCustomersForPosition = (position) => {
    const usedInCurrentDraw = Object.entries(manualWinners)
      .filter(([pos, id]) => Number(pos) !== position && id)
      .map(([, sipId]) => getCustomerIdBySipId(sipId))
      .filter(Boolean);

    return eligibleRunningSips.filter((sip) => {
      const customerId = getCustomerId(sip);
      if (!customerId || usedInCurrentDraw.includes(customerId)) {
        return false;
      }
      return isEligibleForPosition(sip?._id, position);
    });
  };

  // Constants
  const SPIN_DURATION_MS = 7000;
  const NAME_CHANGE_INTERVAL_MS = 500; // Update name every 100ms

  // Reset state when modal opens or SIP ID changes
  useEffect(() => {
    if (isOpen) {
      setIsSpinning(false);
      setWinner(null);
      setCurrentName("Ready!"); // Reset display name
      console.log("plan////////////", plan);

    }
    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isOpen, plan]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCustomerId("");
      if (isMultiWinner) {
        const initial = {};
        winnerPositions.forEach((position) => {
          initial[position] = "";
        });
        setManualWinners(initial);
      } else {
        setManualWinners({});
      }
    }
  }, [isOpen, isMultiWinner, winnerPositions]);

  useEffect(() => {
    if (luckyWinner?.firstName && isSpinning) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsSpinning(false);
      const winnerName =
        luckyWinner?.name ||
        `${luckyWinner?.firstName || ""} ${luckyWinner?.lastName || ""}`.trim();
      setCurrentName(winnerName || "Unknown Winner");
      setWinner(luckyWinner);
    }
  }, [luckyWinner, isSpinning]);

  useEffect(() => {
    if (isMultiWinner && luckyWinners?.length > 0) {
      setWinner({ multi: true });
    }
  }, [luckyWinners, isMultiWinner]);

  // Fetch spin history
  //fetch running sips
  useEffect(() => {
    if (Object.keys(plan).length) {
      dispatch(fetchRunningSipsRequest({ sipplan: plan._id }));
      dispatch(fetchSpinHistoryRequest({ sipplan: plan._id }));
    }
  }, [plan, luckyWinner]);

  useEffect(() => {
    if (isOpen) {
      setIsSpinning(false);
      setWinner(null);
      setCurrentName("Ready!");
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [isOpen]);

  // Derived value for button count
  const startSpin = () => {
    if (isSpinning) return;

    if (!Array.isArray(runningSips) || runningSips.length === 0) {
      toast.info("No active SIPs found.");
      return;
    }

    if (!plan?.enableLuckyDraw) {
      toast.error("Lucky draw is disabled for this SIP plan.");
      return;
    }

    const eligibleCustomers = eligibleRunningSips
      .map((sip) => ({
        id: getCustomerId(sip),
        name: getCustomerName(sip),
      }))
      .filter((customer) => customer.id);

    if (eligibleCustomers.length === 0) {
      toast.error(
        "No eligible customers found. Enable Lucky Draw Eligibility for at least one running SIP."
      );
      return;
    }

    setWinner(null);
    setIsSpinning(true);

    // START NAME CYCLING INTERVAL
    let currentIndex = 0;
    intervalRef.current = setInterval(() => {
      // Cycle through names in the array
      const nextCustomer = eligibleCustomers[currentIndex];
      setCurrentName(nextCustomer.name);

      // Increment index, wrapping around to the start
      currentIndex = (currentIndex + 1) % eligibleCustomers.length;
    }, NAME_CHANGE_INTERVAL_MS); // Update every 100ms
    setTimeout(() => {
      dispatch(
        updateLuckySpinRequest({
          sipplan: plan._id,
          eligibleSipIds: eligibleRunningSips.map((sip) => sip?._id).filter(Boolean),
        })
      );
    }, 6000);

    // STOP SPINNING AND SELECT WINNER AFTER DURATION
    setTimeout(() => {
      // 1. Stop name cycling interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // 2. Stop spinning animation
      setIsSpinning(false);
      // Set the final winner state
      setWinner(luckyWinner ? luckyWinner : {});
      // Set the final displayed name to the winner's name
      setCurrentName(
        luckyWinner?.firstName || luckyWinner?.lastName
          ? `${luckyWinner?.firstName || ""} ${luckyWinner?.lastName || ""}`.trim()
          : "Please Try Again",
      );
    }, SPIN_DURATION_MS);
  };
  const handleClose = () => {
    if (!isSpinning) {
      dispatch(resetLuckySpinState()); // reset Redux lucky spin state
      setWinner(null); // reset local winner state
      setCurrentName("Ready!"); // reset displayed name
      onClose(); // call parent onClose
    }
  };
  const handleManualRun = () => {
    if (isMultiWinner) {
      const missingPosition = winnerPositions.find(
        (position) => !manualWinners[position],
      );
      if (missingPosition) {
        toast.info(
          `Please select a customer for each winner position (1 to ${winnerCount}).`,
        );
        return;
      }

      const selectedIds = winnerPositions.map((position) =>
        getCustomerIdBySipId(manualWinners[position]),
      );
      const uniqueIds = new Set(selectedIds);
      if (uniqueIds.size !== winnerPositions.length) {
        toast.error("The same customer cannot be selected for multiple positions.");
        return;
      }

      for (const position of winnerPositions) {
        if (
          !isEligibleForPosition(
            manualWinners[position],
            position,
          )
        ) {
          toast.error(
            `Selected SIP is not eligible for ${getPositionLabel(position)}.`,
          );
          return;
        }
      }

      setWinner(null);
      setIsSpinning(false);
      dispatch(
        updateLuckySpinRequest({
          sipplan: plan._id,
          winners: winnerPositions.map((position) => ({
            position,
            commonSipId: manualWinners[position],
            customerId: getCustomerIdBySipId(manualWinners[position]),
          })),
        }),
      );
      return;
    }

    if (!selectedCustomerId) {
      toast.info("Please select a customer");
      return;
    }

    setWinner(null);
    setIsSpinning(false);

    dispatch(
      updateLuckySpinRequest({
        sipplan: plan._id,
        commonSipId: selectedCustomerId,
        customerId: getCustomerIdBySipId(selectedCustomerId),
      }),
    );
  };
  useEffect(() => {
    if (isOpen) {
      setMode(null); // reset mode
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={isSpinning ? () => {} : onClose}
      aria-labelledby="lucky-spin-modal-title"
      aria-describedby="lucky-spin-modal-description"
    >
      <Box sx={style}>
        {/* LEFT COLUMN: Spin History */}
        <LeftColumn>
          <Box mb={2}>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Spin History
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#1976d2" }}
            >
              Plan Name :- {plan?.name || "SIP Plan"}
            </Typography>
          </Box>

          {spinHistory && spinHistory.length > 0 ? (
            <HistoryGrid>
              {spinHistory.map((item) => (
                <HistoryCard key={item._id}>
                  {/* Heading Row */}
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                    🎁 Lucky Draw Details
                  </Typography>

                  {/* Customer Name */}
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Customer Name:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {item.customer?.firstName
                      ? `${item.customer.firstName} ${item.customer.lastName}`
                      : "Unknown"}
                  </Typography>

                  {/* Date */}
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Date:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {item.createdAt
                      ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "Date N/A"}
                  </Typography>

                  {/* Position */}
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Position:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {item.position === 1
                      ? "Main Winner"
                      : item.position
                        ? `Additional Winner (${item.position})`
                        : "Main Winner"}
                  </Typography>

                  {/* Gift Type */}
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Gift Type:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.type || "N/A"}
                  </Typography>
                </HistoryCard>
              ))}
            </HistoryGrid>
          ) : (
            <Typography color="text.secondary">
              No spin history available.
            </Typography>
          )}
        </LeftColumn>

        {/* RIGHT COLUMN: Spinner / Winner / Start Button */}
        <RightColumn>
          {mode === null && (
            <Paper
              elevation={3}
              sx={{
                p: 4,
                width: 320,
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" mb={3} fontWeight="bold">
                Select Lucky Draw Mode
              </Typography>

              <Button
                fullWidth
                variant="contained"
                sx={{ mb: 2 }}
                onClick={() => setMode("random")}
              >
                🎲 Random Spin
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setWinner(null);
                  setMode("manual");
                }}
              >
                🎯 Manual Select
              </Button>
            </Paper>
          )}
          {mode === "manual" && (
            <>
              {luckyWinner && luckyWinner?.firstName ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    width: isMultiWinner ? 420 : 320,
                    borderRadius: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h5" mb={2}>
                    🎉 Manual Lucky Winner{isMultiWinner ? "s" : ""}
                  </Typography>

                  {isMultiWinner && luckyWinners?.length > 0 ? (
                    luckyWinners.map((entry) => (
                      <Box key={entry.position} mb={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {getPositionLabel(entry.position)}
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {entry.customerData?.firstName}{" "}
                          {entry.customerData?.lastName}
                        </Typography>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="h4" color="success.main">
                      {luckyWinner?.firstName} {luckyWinner?.lastName}
                    </Typography>
                  )}

                  <Button
                    sx={{ mt: 3 }}
                    variant="outlined"
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </Paper>
              ) : (
                <>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      mb: 3,
                      width: isMultiWinner ? 420 : 320,
                      borderRadius: 3,
                      background: "#f9fbff",
                    }}
                  >
                    <Typography variant="h6" mb={2} fontWeight="bold">
                      🎯 {isMultiWinner ? "Select Winners by Position" : "Select Customer"}
                    </Typography>

                    {isMultiWinner ? (
                      winnerPositions.map((position) => (
                        <FormControl
                          key={position}
                          fullWidth
                          size="small"
                          sx={{ mb: 2 }}
                        >
                          <InputLabel>{getPositionLabel(position)}</InputLabel>
                          <Select
                            value={manualWinners[position] || ""}
                            label={getPositionLabel(position)}
                            onChange={(e) =>
                              setManualWinners((prev) => ({
                                ...prev,
                                [position]: e.target.value,
                              }))
                            }
                            disabled={isSpinning}
                          >
                            {getEligibleCustomersForPosition(position).map(
                              (sip) => (
                                <MenuItem
                                  key={`${position}-${sip._id}`}
                                  value={sip._id}
                                >
                                  {getCustomerName(sip)}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      ))
                    ) : (
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Customer</InputLabel>
                        <Select
                          value={selectedCustomerId}
                          label="Select Customer"
                          onChange={(e) => setSelectedCustomerId(e.target.value)}
                          disabled={isSpinning}
                        >
                          {eligibleRunningSips.map((sip) => (
                            <MenuItem key={sip._id} value={sip._id}>
                              {getCustomerName(sip)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {(isMultiWinner
                      ? winnerPositions.every((position) => manualWinners[position])
                      : selectedCustomerId) && (
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={handleManualRun}
                        disabled={loading}
                      >
                        {isMultiWinner
                          ? "Confirm Multi-Winner Draw"
                          : "Run Selected Customer"}
                      </Button>
                    )}
                  </Paper>

                  <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </>
          )}
          {mode === "random" && (
            <Paper
              elevation={3}
              sx={{
                p: 3,
                width: 320,
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              {" "}
              <Typography
                id="lucky-spin-modal-title"
                variant="h5"
                component="h2"
                mb={2}
              >
                {winner ? "🎉 Lucky Winner! 🎉" : "Lucky Draw Customer"}
              </Typography>
              <Typography
                id="lucky-spin-modal-title"
                variant="h6"
                component="h4"
                mb={2}
              >
                {winner ? "" : "spin to create new lucky draw"}
              </Typography>
              {loading ? (
                <CircularProgress color="primary" />
              ) : winner ? (
                <Box textAlign="center">
                  <Typography variant="h6" color="text.secondary">
                    {isMultiWinner ? "Lucky Draw Winners:" : "The Lucky Winner is:"}
                  </Typography>
                  {isMultiWinner && luckyWinners?.length > 0 ? (
                    luckyWinners.map((entry) => (
                      <Box key={entry.position} mt={1} mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          {getPositionLabel(entry.position)}
                        </Typography>
                        <Typography variant="h5" color="success.main">
                          {entry.customerData?.firstName}{" "}
                          {entry.customerData?.lastName}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="h3" color="success.main" mt={1} mb={2}>
                      {luckyWinner?.firstName} {luckyWinner?.lastName}
                    </Typography>
                  )}
                </Box>
              ) : (
                <FancySpinner
                  onClick={() => startSpin()}
                  isSpinning={isSpinning}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontWeight: "bold",
                      color: "#333",
                    }}
                  >
                    {currentName}
                  </Typography>
                </FancySpinner>
              )}
              <Button
                variant="outlined"
                color={winner ? "primary" : "inherit"}
                onClick={handleClose}
                sx={{ mt: 3 }}
                disabled={isSpinning}
              >
                {winner ? "Close" : "Cancel"}
              </Button>
            </Paper>
          )}
        </RightColumn>
      </Box>
    </Modal>
  );
};

export default LuckySpinModal;
